import { useState } from "react";
import { Link } from "react-router-dom";
import { Award, Download } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function NameForm({ token, initial, label, onSaved, onCancel }) {
  const [name, setName] = useState(initial || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { setErr("Enter the name to print."); return; }
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/coach-ready/learn/${token}/certificate`, { name: name.trim() }); onSaved(data); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Something went wrong. Try again."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-3 space-y-3" noValidate>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Your name as it should be printed"
        data-testid="cr-cert-name" className="w-full border-2 border-[#D3CFC0] bg-white px-4 py-3 text-[#1E1E1E] focus:outline-none focus:border-[#1E1E1E]" />
      {err && <p className="text-sm text-[#B23A2E]" data-testid="cr-cert-error">{err}</p>}
      <div className="flex gap-3">
        <button type="submit" disabled={busy} data-testid="cr-cert-submit"
          className="flex-1 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3.5 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
          {busy ? "Creating…" : label}
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="px-4 text-sm font-bold text-[#3A3A3A]" data-testid="cr-cert-cancel">Cancel</button>}
      </div>
    </form>
  );
}

export const CertificatePanel = ({ token, cert, onChange }) => {
  const [editing, setEditing] = useState(false);
  if (!cert) return (
    <div className="border-2 border-[#2F7A4B] bg-white p-5" data-testid="cr-cert-create">
      <span className="flex items-center gap-2 font-head uppercase text-xl text-[#1E1E1E]"><Award size={20} className="text-[#DB9130]" />All six lessons complete</span>
      <p className="mt-2 text-sm text-[#3A3A3A]">Type your name exactly as you want it on your Coach Ready certificate of completion. We&apos;ll also email you a copy.</p>
      <NameForm token={token} label="Create my certificate" onSaved={onChange} />
    </div>
  );
  return (
    <div className="border-2 border-[#1E1E1E] bg-white p-5" data-testid="cr-cert-ready">
      <span className="flex items-center gap-2 font-head uppercase text-xl text-[#1E1E1E]"><Award size={20} className="text-[#DB9130]" />Your certificate of completion</span>
      <p className="mt-2 text-sm text-[#3A3A3A]"><strong data-testid="cr-cert-printed-name">{cert.name}</strong> · {cert.completed_date} · <span className="whitespace-nowrap">ID <span data-testid="cr-cert-id">{cert.id}</span></span></p>
      <a href={`${BACKEND}/api/coach-ready/learn/${token}/certificate.pdf`} data-testid="cr-cert-download"
        className="mt-4 flex items-center justify-center gap-2 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3.5 hover:bg-[#D6922C] transition-colors">
        <Download size={16} /> Download certificate (PDF)
      </a>
      <Link to={cert.verify_path} className="mt-3 inline-block link-line text-sm" data-testid="cr-cert-verify-link">Public check page</Link>
      {cert.name_changes === 0 && !editing && (
        <button onClick={() => setEditing(true)} className="mt-3 block text-xs text-[#6E6E6E] underline" data-testid="cr-cert-fix-name">Fix a typo in your name (one time)</button>
      )}
      {editing && <NameForm token={token} initial={cert.name} label="Save corrected name" onSaved={(c) => { setEditing(false); onChange(c); }} onCancel={() => setEditing(false)} />}
    </div>
  );
};
